import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Search, Package } from "lucide-react";
import { api, formatError } from "../lib/api.js";
import { useAuth } from "../lib/auth.jsx";
import { fmtDate } from "../lib/format.js";
import { VehicleTriplePicker } from "../lib/vehicleCatalogue.jsx";

const STATUS_STYLE = {
  "OPEN": "bg-blue-50 text-blue-800 border-blue-300",
  "REJECTED": "bg-red-50 text-red-800 border-red-300",
  "ORDERED": "bg-indigo-50 text-indigo-800 border-indigo-300",
  "ALOCADO": "bg-purple-50 text-purple-800 border-purple-300",
  "IN TRANSIT": "bg-yellow-50 text-yellow-800 border-yellow-300",
  "RECEIVED": "bg-teal-50 text-teal-800 border-teal-300",
  "INCOMPLETE": "bg-orange-50 text-orange-800 border-orange-300",
  "READY FOR COLLECTION": "bg-emerald-50 text-emerald-800 border-emerald-300",
  "CLIENT CONTACTED": "bg-green-50 text-green-800 border-green-300",
  "CLOSED": "bg-gray-100 text-gray-700 border-gray-300",
  "REOPENED": "bg-cyan-50 text-cyan-800 border-cyan-300",
};

const TRANSIT_LABEL = {
  em_transito: "Em trânsito",
  nas_alfandegas: "Nas alfândegas",
  desalfandegado: "Desalfandegado",
};

const CREATE_ROLES = ["admin", "parts_user", "department_manager", "national_aftersales_manager"];

export default function PartsBenchOrdersPage() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q.trim()) params.append("q", q.trim());
      if (status) params.append("status", status);
      const { data } = await api.get(`/parts-bench-orders?${params}`);
      setRows(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [q, status]);

  const canCreate = CREATE_ROLES.includes(user?.role);

  return (
    <div className="p-4 sm:p-6 max-w-[1600px] mx-auto">
      <div className="flex items-end justify-between mb-4 gap-3 flex-wrap">
        <div>
          <h1 className="font-heading text-2xl font-bold text-[#1A1A2E] flex items-center gap-2">
            <Package className="w-6 h-6 text-[#1F3864]" strokeWidth={1.8} />
            Encomendas de Balcão (PEB)
          </h1>
          <p className="text-sm text-[#6C757D] mt-1">{loading ? "…" : `${rows.length} encomenda${rows.length === 1 ? "" : "s"}`}</p>
        </div>
        {canCreate && (
          <button onClick={() => setShowNew(true)} className="bg-[#1F3864] hover:bg-[#142542] text-white rounded-sm px-3 py-2 text-sm font-medium flex items-center gap-2" data-testid="peb-new-btn">
            <Plus className="w-4 h-4" /> Nova encomenda
          </button>
        )}
      </div>

      <div className="bg-white border border-gray-200 rounded-md p-3 mb-4 grid grid-cols-1 md:grid-cols-3 gap-3">
        <label className="block md:col-span-2">
          <span className="text-[10px] uppercase tracking-widest text-[#6C757D]">Pesquisar</span>
          <div className="relative mt-0.5">
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="PEB, ref ERP, cliente, telefone…" className="w-full pl-8 pr-3 py-1.5 border border-gray-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-[#1F3864]/30" data-testid="peb-search" />
            <Search className="w-4 h-4 text-[#6C757D] absolute left-2 top-1/2 -translate-y-1/2" />
          </div>
        </label>
        <label className="block">
          <span className="text-[10px] uppercase tracking-widest text-[#6C757D]">Estado</span>
          <select value={status} onChange={(e) => setStatus(e.target.value)} className="w-full mt-0.5 px-2 py-1.5 border border-gray-300 rounded-sm bg-white text-sm" data-testid="peb-filter-status">
            <option value="">Todos</option>
            {Object.keys(STATUS_STYLE).map(s => <option key={s}>{s}</option>)}
          </select>
        </label>
      </div>

      <div className="bg-white border border-gray-200 rounded-md overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#F4F6F8] text-[10px] uppercase tracking-widest text-[#6C757D]">
            <tr><Th>PEB</Th><Th>Ref ERP</Th><Th>Cliente</Th><Th>Telefone</Th><Th>Estado</Th><Th>Sub-fase</Th><Th>Import.</Th><Th>Dealer</Th><Th>Criado</Th></tr>
          </thead>
          <tbody>
            {loading ? <tr><td colSpan={9} className="text-center py-8 text-[#6C757D]">A carregar…</td></tr>
              : rows.length === 0 ? <tr><td colSpan={9} className="text-center py-8 text-[#6C757D]" data-testid="peb-empty">Sem encomendas</td></tr>
              : rows.map(r => (
                <tr key={r.id} onClick={() => nav(`/parts-bench-orders/${r.id}`)} data-testid={`peb-row-${r.id}`} className="border-t border-gray-100 hover:bg-[#F4F6F8] cursor-pointer">
                  <Td mono className="text-[#1F3864] font-semibold">{r.number}</Td>
                  <Td mono>{r.erp_reference}</Td>
                  <Td>{r.customer_name}{r.customer && <span className="ml-1 text-[10px] text-[#1F3864] font-mono">({r.customer.customer_number})</span>}</Td>
                  <Td className="text-[#6C757D]">{r.customer_phone}</Td>
                  <Td><span className={`inline-flex text-[10px] font-semibold uppercase tracking-wider border rounded-sm px-1.5 py-0.5 ${STATUS_STYLE[r.current_status] || ""}`}>{r.current_status}</span></Td>
                  <Td className="text-[10px] text-[#6C757D]">{r.current_status === "IN TRANSIT" && r.transit_stage ? TRANSIT_LABEL[r.transit_stage] : "—"}</Td>
                  <Td>{r.is_import ? "Sim" : "—"}</Td>
                  <Td>{r.branch}</Td>
                  <Td className="text-[#6C757D] text-xs">{fmtDate(r.created_at)}</Td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {showNew && <NewPebDialog onClose={() => setShowNew(false)} onCreated={(r) => { setShowNew(false); nav(`/parts-bench-orders/${r.id}`); }} />}
    </div>
  );
}

function Th({ children }) { return <th className="text-left px-3 py-2 whitespace-nowrap">{children}</th>; }
function Td({ children, mono, className = "" }) { return <td className={`px-3 py-2 whitespace-nowrap ${mono ? "font-mono" : ""} ${className}`}>{children}</td>; }

function NewPebDialog({ onClose, onCreated }) {
  const [enums, setEnums] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [form, setForm] = useState({
    erp_reference: "", customer_id: "", customer_name: "", customer_phone: "", customer_email: "",
    is_import: false, branch: "", priority: "Normal", notes: "",
    brand: "", model: "", vehicle_generation: "",
  });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/enums").then(r => setEnums(r.data));
    api.get("/customers").then(r => setCustomers(r.data));
  }, []);
  useEffect(() => { if (enums && !form.branch) setForm(f => ({ ...f, branch: enums.branches[0] || "" })); }, [enums]);

  const pickCli = (id) => {
    const c = customers.find(x => x.id === id);
    if (!c) { setForm(f => ({ ...f, customer_id: "" })); return; }
    setForm(f => ({ ...f, customer_id: id, customer_name: c.name, customer_phone: c.phone, customer_email: c.email || "" }));
  };
  const submit = async () => {
    setErr(""); setBusy(true);
    try {
      const { data } = await api.post("/parts-bench-orders", { ...form, customer_id: form.customer_id || null });
      onCreated(data);
    } catch (e) { setErr(formatError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  if (!enums) return null;
  const inp = "w-full mt-0.5 px-2 py-1.5 border border-gray-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-[#1F3864]/30";
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-md shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="new-peb-dialog">
        <div className="sticky top-0 bg-white px-5 py-3 border-b border-gray-200 flex items-center justify-between">
          <h2 className="font-heading text-lg font-bold">Nova encomenda de balcão</h2>
          <button onClick={onClose} className="p-1 hover:bg-[#F4F6F8] rounded">✕</button>
        </div>
        <div className="p-5 grid grid-cols-2 gap-3">
          <div className="col-span-2 bg-[#F4F6F8] border-l-2 border-[#1F3864] px-3 py-2 text-xs text-[#6C757D]">
            O cliente deve ter pago no ERP antes de criares a encomenda. A referência ERP é obrigatória e única.
          </div>
          <Fld label="Referência ERP *"><input value={form.erp_reference} onChange={e => setForm({ ...form, erp_reference: e.target.value })} className={`${inp} font-mono`} data-testid="peb-erp-ref" /></Fld>
          <Fld label="Dealer *"><select value={form.branch} onChange={e => setForm({ ...form, branch: e.target.value })} className={inp}>{enums.branches.map(b => <option key={b}>{b}</option>)}</select></Fld>
          <Fld label="Cliente CLI (opcional)"><select value={form.customer_id} onChange={e => pickCli(e.target.value)} className={inp} data-testid="peb-cli-select">
            <option value="">— Ad-hoc (passante) —</option>
            {customers.map(c => <option key={c.id} value={c.id}>{c.customer_number} · {c.name}</option>)}
          </select></Fld>
          <Fld label="Prioridade"><select value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })} className={inp}><option>Normal</option><option>Alta</option><option>Urgente</option></select></Fld>
          <Fld label="Nome do cliente *"><input value={form.customer_name} onChange={e => setForm({ ...form, customer_name: e.target.value })} className={inp} data-testid="peb-customer-name" /></Fld>
          <Fld label="Telefone *"><input value={form.customer_phone} onChange={e => setForm({ ...form, customer_phone: e.target.value })} className={inp} /></Fld>
          <Fld label="Email"><input type="email" value={form.customer_email} onChange={e => setForm({ ...form, customer_email: e.target.value })} className={inp} /></Fld>
          <div className="col-span-2 flex items-center gap-2 pt-2">
            <input type="checkbox" id="peb-imp" checked={form.is_import} onChange={e => setForm({ ...form, is_import: e.target.checked })} data-testid="peb-is-import" />
            <label htmlFor="peb-imp" className="text-sm">É encomenda de importação (poderá passar pelas alfândegas)</label>
          </div>
          <div className="col-span-2">
            <label className="block text-[11px] text-gray-600 mb-0.5">Viatura associada (Marca · Modelo · Geração) — opcional</label>
            <VehicleTriplePicker
              brand={form.brand}
              model={form.model}
              generation={form.vehicle_generation}
              onChange={({ brand, model, generation }) => setForm(f => ({ ...f, brand: brand || "", model: model || "", vehicle_generation: generation || "" }))}
              testidPrefix="peb"
            />
          </div>
          <div className="col-span-2"><Fld label="Notas"><textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} className={`${inp} h-20`} /></Fld></div>
          {err && <div className="col-span-2 text-xs text-[#721C24] bg-[#F8D7DA] border border-[#DC3545]/30 rounded-sm px-3 py-2" data-testid="peb-error">{err}</div>}
        </div>
        <div className="sticky bottom-0 bg-white px-5 py-3 border-t flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-gray-300 rounded-sm">Cancelar</button>
          <button onClick={submit} disabled={busy} className="px-4 py-2 text-sm bg-[#1F3864] text-white rounded-sm disabled:opacity-60" data-testid="peb-submit">{busy ? "…" : "Criar encomenda"}</button>
        </div>
      </div>
    </div>
  );
}

function Fld({ label, children }) { return <label className="block"><span className="text-[10px] uppercase tracking-widest text-[#6C757D]">{label}</span>{children}</label>; }
